import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";

import { PlaybackControls } from "../..";
import { SelectionsPanel } from "./SelectionsPanel";

interface Pitch {
  id: string;
  frames: any[];
  metadata: Record<string, any>;
}

interface Skeleton {
  group: THREE.Group;
  joints: Record<string, THREE.Mesh>;
  lines: Array<{
    line: THREE.Mesh;
    start: string;
    end: string;
    geometry: THREE.CylinderGeometry;
  }>;
  userData?: Record<string, any>;
}

interface PlaybackState {
  isPlaying: boolean;
  playbackSpeed: number;
  minTime: number;
  maxTime: number;
  currentTime: number;
}

interface PlaybackHandlers {
  onPlayPause: () => void;
  onPlaybackSpeedChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSkipBackward: () => void;
  onSkipForward: () => void;
  onTimelineChange: (time: number) => void;
}

interface AvatarState {
  offsets: Record<string, { x: number; y: number; z: number }>;
  rotations: Record<string, { x: number; y: number; z: number }>;
}

interface SelectionHandlers {
  onColorChangeAll: (color: string) => void;
  onOffsetChangeAll: (axis: "x" | "y" | "z", value: number) => void;
  onRotationChangeAll: (axis: "x" | "y" | "z", value: number) => void;
}

interface ThreeDViewProps {
  canvas3dRef: React.RefObject<HTMLCanvasElement | null>;
  selectedPitches: Set<string>;
  pitches: Record<string, Pitch>;
  movements: any[];
  colorPalette: string[];
  disabled: boolean;
  avatarState: AvatarState;
  playbackState: PlaybackState;
  playbackHandlers: PlaybackHandlers;
  selectionHandlers: SelectionHandlers;
  isHittingMode?: boolean;
}

export const ThreeDView: React.FC<ThreeDViewProps> = ({
  canvas3dRef,
  selectedPitches,
  pitches,
  movements,
  colorPalette,
  disabled,
  avatarState,
  playbackState,
  playbackHandlers,
  selectionHandlers,
  isHittingMode = false,
}) => {
  const [webglUnavailable, setWebglUnavailable] = useState(false);
  const { offsets: avatarOffsets, rotations: avatarRotations } = avatarState;
  const { isPlaying, playbackSpeed, minTime, maxTime, currentTime } =
    playbackState;
  const {
    onPlayPause,
    onPlaybackSpeedChange,
    onSkipBackward,
    onSkipForward,
    onTimelineChange,
  } = playbackHandlers;

  // 3D View configuration constants
  const VIEW_CONSTANTS = useMemo(
    () => ({
      JOINT_SIZE: 0.05,
      BONE_RADIUS: 0.01,
      SCALE_FACTOR: 1.5,
      CENTER_OFFSET_X: 0.08,
      CENTER_OFFSET_Y: 1.0,
      CENTER_OFFSET_Z: 0,
      DEFAULT_COLOR: "#FF6B6B",
      GRID_SIZE: 10,
      GRID_DIVISIONS: 10,
    }),
    [],
  );

  // Joint connections for skeleton - simplified human skeleton
  const connections = useMemo(() => {
    const baseConnections = [
      // Head to shoulders
      ["NECK", "LSJC"],
      ["NECK", "RSJC"],
      // Left arm
      ["LSJC", "LEJC"],
      ["LEJC", "LWJC"],
      // Right arm
      ["RSJC", "REJC"],
      ["REJC", "RWJC"],
      // Spine
      ["NECK", "MIDHIP"],
      // Left leg
      ["MIDHIP", "LHJC"],
      ["LHJC", "LKJC"],
      ["LKJC", "LAJC"],
      // Right leg
      ["MIDHIP", "RHJC"],
      ["RHJC", "RKJC"],
      ["RKJC", "RAJC"],
    ];

    // Add bat connection only for hitting mode
    if (isHittingMode) {
      baseConnections.push(["BHANDLE", "BHEAD"]);
    }

    return baseConnections;
  }, [isHittingMode]);

  // Three.js refs
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const renderIdRef = useRef<number | null>(null);
  const skeletonsRef = useRef<Record<string, Skeleton>>({});
  const mouseDownRef = useRef(false);
  const mouseXRef = useRef(0);
  const mouseYRef = useRef(0);
  const cameraTargetRef = useRef(new THREE.Vector3(0, 1, 0));

  // Create skeleton for a pitch
  const createSkeleton = useCallback(
    (pitchId: string, color: string): Skeleton => {
      const group = new THREE.Group();
      const joints: Record<string, THREE.Mesh> = {};
      const lines: Array<{
        line: THREE.Mesh;
        start: string;
        end: string;
        geometry: THREE.CylinderGeometry;
      }> = [];

      // Create spheres for joints
      const jointNames = [
        "NECK",
        "LSJC",
        "LEJC",
        "LWJC",
        "RSJC",
        "REJC",
        "RWJC",
        "MIDHIP",
        "LHJC",
        "LKJC",
        "LAJC",
        "RHJC",
        "RKJC",
        "RAJC",
        // Add bat joints only for hitting mode
        ...(isHittingMode ? ["BHANDLE", "BHEAD"] : []),
      ];

      // Create spheres for joints
      const jointGeometry = new THREE.SphereGeometry(
        VIEW_CONSTANTS.JOINT_SIZE,
        8,
        6,
      );
      const jointMaterial = new THREE.MeshPhongMaterial({ color });

      for (const name of jointNames) {
        const joint = new THREE.Mesh(jointGeometry, jointMaterial);
        joint.renderOrder = 2; // Render joints after lines
        joints[name] = joint;
        group.add(joint);
      }

      // Create lines for bones using cylinders
      for (const conn of connections) {
        const cylinderGeometry = new THREE.CylinderGeometry(
          VIEW_CONSTANTS.BONE_RADIUS,
          VIEW_CONSTANTS.BONE_RADIUS,
          1,
          4,
        );
        const cylinderMaterial = new THREE.MeshPhongMaterial({ color });
        const cylinder = new THREE.Mesh(cylinderGeometry, cylinderMaterial);
        cylinder.renderOrder = 0; // Render cylinders first
        lines.push({
          line: cylinder,
          start: conn[0],
          end: conn[1],
          geometry: cylinderGeometry,
        });
        group.add(cylinder);
      }

      return { group, joints, lines };
    },
    [connections, VIEW_CONSTANTS, isHittingMode],
  );

  // Update joint positions with rotation applied
  const updateJointPositions = useCallback(
    (
      skeleton: Skeleton,
      frameData: any,
      offsets: any,
      rotationMatrix: THREE.Matrix4,
    ) => {
      const jointNames = Object.keys(skeleton.joints);
      for (const jointName of jointNames) {
        const joint = skeleton.joints[jointName];
        const x = frameData[jointName + "_X"] || 0;
        const y = frameData[jointName + "_Y"] || 0;
        const z = frameData[jointName + "_Z"] || 0;

        const position = new THREE.Vector3(
          (x - VIEW_CONSTANTS.CENTER_OFFSET_X) * VIEW_CONSTANTS.SCALE_FACTOR +
            offsets.x,
          (z - VIEW_CONSTANTS.CENTER_OFFSET_Y) * VIEW_CONSTANTS.SCALE_FACTOR +
            VIEW_CONSTANTS.CENTER_OFFSET_Y +
            offsets.z,
          -y * VIEW_CONSTANTS.SCALE_FACTOR + offsets.y,
        );
        position.applyMatrix4(rotationMatrix);
        joint.position.copy(position);
      }
    },
    [VIEW_CONSTANTS],
  );

  // Update bone connections
  const updateBoneConnections = useCallback((skeleton: Skeleton) => {
    for (const lineInfo of skeleton.lines) {
      const cylinder = lineInfo.line;
      const start = lineInfo.start;
      const end = lineInfo.end;
      const startJoint = skeleton.joints[start];
      const endJoint = skeleton.joints[end];

      const startPos = startJoint.position;
      const endPos = endJoint.position;

      cylinder.position.x = (startPos.x + endPos.x) / 2;
      cylinder.position.y = (startPos.y + endPos.y) / 2;
      cylinder.position.z = (startPos.z + endPos.z) / 2;

      const dx = endPos.x - startPos.x;
      const dy = endPos.y - startPos.y;
      const dz = endPos.z - startPos.z;
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

      cylinder.scale.y = distance;
      cylinder.lookAt(endPos);
      cylinder.rotateX(Math.PI / 2);
    }
  }, []);

  // Update skeleton position based on frame data
  const updateSkeleton = useCallback(
    (skeleton: Skeleton, frameData: any, movementId: string) => {
      if (!frameData) return;

      // Get avatar offsets and rotations for this movement
      const offsets = avatarOffsets[movementId] || { x: 0, y: 0, z: 0 };
      const rotations = avatarRotations[movementId] || { x: 0, y: 0, z: 0 };

      // Create rotation matrix
      const rotationMatrix = new THREE.Matrix4();
      rotationMatrix.makeRotationFromEuler(
        new THREE.Euler(
          (rotations.x * Math.PI) / 180,
          (rotations.y * Math.PI) / 180,
          (rotations.z * Math.PI) / 180,
          "XYZ",
        ),
      );

      // Update joint positions and bone connections
      updateJointPositions(skeleton, frameData, offsets, rotationMatrix);
      updateBoneConnections(skeleton);

      skeleton.group.rotation.set(0, 0, 0);
    },
    [
      avatarOffsets,
      avatarRotations,
      updateJointPositions,
      updateBoneConnections,
    ],
  );

  // Update skeleton color
  const updateSkeletonColor = useCallback(
    (skeleton: Skeleton, color: string) => {
      skeleton.userData = { ...skeleton.userData, color };

      // Parse hex color for Three.js
      const threeColor = new THREE.Color(color);

      // Update joint colors
      Object.values(skeleton.joints).forEach((joint) => {
        if (joint.material) {
          (joint.material as THREE.MeshPhongMaterial).color.copy(threeColor);
        }
      });

      // Update bone colors
      skeleton.lines.forEach((lineData) => {
        if (lineData.line.material) {
          (lineData.line.material as THREE.MeshPhongMaterial).color.copy(
            threeColor,
          );
        }
      });
    },
    [],
  );

  const removeUnselectedSkeletons = useCallback(() => {
    if (!sceneRef.current) return;

    for (const skeletonId of Object.keys(skeletonsRef.current)) {
      if (!selectedPitches.has(skeletonId)) {
        const skeleton = skeletonsRef.current[skeletonId];
        sceneRef.current.remove(skeleton.group);
        delete skeletonsRef.current[skeletonId];
      }
    }
  }, [selectedPitches]);

  const findClosestFrame = useCallback((pitch: any, time: number) => {
    if (!pitch.frames || pitch.frames.length === 0) return null;

    return pitch.frames.reduce((closest: any, frame: any) => {
      const frameTime = frame.time_from_max_hand || 0;
      const timeDiff = Math.abs(frameTime - time);
      const closestTimeDiff = closest
        ? Math.abs(closest.time_from_max_hand - time)
        : Infinity;
      return timeDiff < closestTimeDiff ? frame : closest;
    }, null);
  }, []);

  const getSkeletonColor = useCallback(
    (pitchId: string, currentColor?: string) => {
      if (currentColor) return currentColor;
      // Default color if no color is provided
      return "#FF6B6B";
    },
    [],
  );

  const createNewSkeleton = useCallback(
    (pitchId: string, color: string) => {
      if (!sceneRef.current) return;

      const skeleton = createSkeleton(pitchId, color);
      skeleton.userData = { color };
      skeletonsRef.current[pitchId] = skeleton;
      sceneRef.current.add(skeleton.group);
    },
    [createSkeleton],
  );

  const updateExistingSkeleton = useCallback(
    (pitchId: string, currentColor?: string) => {
      const skeleton = skeletonsRef.current[pitchId];
      if (currentColor && skeleton.userData?.color !== currentColor) {
        updateSkeletonColor(skeleton, currentColor);
      }
    },
    [updateSkeletonColor],
  );

  const processSkeletonForPitch = useCallback(
    (pitchId: string, time: number) => {
      const pitch = pitches[pitchId];
      if (!pitch) return;

      const closestFrame = findClosestFrame(pitch, time);
      if (!closestFrame) return;

      const movement = movements.find((m) => m.id === pitchId);
      const currentColor = movement?.color;

      if (!skeletonsRef.current[pitchId]) {
        const color = getSkeletonColor(pitchId, currentColor);
        createNewSkeleton(pitchId, color);
      } else {
        updateExistingSkeleton(pitchId, currentColor);
      }

      const skeleton = skeletonsRef.current[pitchId];
      updateSkeleton(skeleton, closestFrame, pitchId);
    },
    [
      pitches,
      movements,
      findClosestFrame,
      getSkeletonColor,
      createNewSkeleton,
      updateExistingSkeleton,
      updateSkeleton,
    ],
  );

  // Update skeletons at current time
  const updateSkeletonsAtTime = useCallback(
    (time: number) => {
      if (!sceneRef.current) return;

      removeUnselectedSkeletons();

      for (const pitchId of selectedPitches) {
        processSkeletonForPitch(pitchId, time);
      }
    },
    [selectedPitches, removeUnselectedSkeletons, processSkeletonForPitch],
  );

  // Main render loop for Three.js
  useEffect(() => {
    const renderLoop = () => {
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
      renderIdRef.current = requestAnimationFrame(renderLoop);
    };

    renderIdRef.current = requestAnimationFrame(renderLoop);

    return () => {
      if (renderIdRef.current) {
        cancelAnimationFrame(renderIdRef.current);
      }
    };
  }, []);

  // Update skeletons when time changes
  useEffect(() => {
    updateSkeletonsAtTime(currentTime);
  }, [currentTime, updateSkeletonsAtTime]);

  // Set up mouse controls for 3D view
  useEffect(() => {
    if (!canvas3dRef.current) return;

    const canvas = canvas3dRef.current;

    const handleMouseDown = (e: MouseEvent) => {
      mouseDownRef.current = true;
      mouseXRef.current = e.clientX;
      mouseYRef.current = e.clientY;
    };

    const handleMouseUp = () => {
      mouseDownRef.current = false;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (
        !mouseDownRef.current ||
        !cameraRef.current ||
        !cameraTargetRef.current
      )
        return;

      const camera = cameraRef.current;
      const cameraTarget = cameraTargetRef.current;
      const deltaX = e.clientX - mouseXRef.current;
      const deltaY = e.clientY - mouseYRef.current;

      // Rotate camera around target
      const spherical = new THREE.Spherical();
      spherical.setFromVector3(camera.position.clone().sub(cameraTarget));
      spherical.theta -= deltaX * 0.01;
      spherical.phi += deltaY * 0.01;
      spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));

      const newPosition = new THREE.Vector3();
      newPosition.setFromSpherical(spherical);
      newPosition.add(cameraTarget);

      camera.position.copy(newPosition);
      camera.lookAt(cameraTarget);

      mouseXRef.current = e.clientX;
      mouseYRef.current = e.clientY;
    };

    // Mouse wheel event (zoom)
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (!cameraRef.current || !cameraTargetRef.current) return;

      const camera = cameraRef.current;
      const cameraTarget = cameraTargetRef.current;
      const scale = e.deltaY > 0 ? 1.1 : 0.9;
      const offset = camera.position.clone().sub(cameraTarget);
      offset.multiplyScalar(scale);
      camera.position.copy(cameraTarget.clone().add(offset));
    };

    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mouseup", handleMouseUp);
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("wheel", handleWheel);

    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mouseup", handleMouseUp);
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("wheel", handleWheel);
    };
  }, [canvas3dRef]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!canvas3dRef.current) return;

    const canvas = canvas3dRef.current;
    const container = canvas.parentElement;
    if (!container) return;

    let renderer: THREE.WebGLRenderer | null = null;

    try {
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x222222);
      sceneRef.current = scene;

      const camera = new THREE.PerspectiveCamera(
        75,
        container.clientWidth / container.clientHeight,
        0.1,
        1000,
      );
      camera.position.set(3, 1.5, 3);
      camera.lookAt(0, 1, 0);
      cameraRef.current = camera;

      renderer = new THREE.WebGLRenderer({
        canvas: canvas3dRef.current,
        antialias: true,
      });
      renderer.setSize(container.clientWidth, container.clientHeight);
      rendererRef.current = renderer;
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(10, 10, 5);
      scene.add(directionalLight);

      const gridHelper = new THREE.GridHelper(10, 10, 0x444444, 0x333333);
      scene.add(gridHelper);

      // Handle container resize (both window and container size changes)
      const handleResize = () => {
        if (!container || !camera || !renderer) return;

        const width = container.clientWidth;
        const height = container.clientHeight;

        // Prevent division by zero
        if (width === 0 || height === 0) return;

        camera.aspect = width / height;
        camera.updateProjectionMatrix();

        renderer.setSize(width, height);
      };

      // Use ResizeObserver to handle container size changes (including during drag)
      let resizeObserver: ResizeObserver | null = null;
      if (container) {
        resizeObserver = new ResizeObserver(() => {
          // Throttle resize updates
          requestAnimationFrame(handleResize);
        });
        resizeObserver.observe(container);
      }

      // Also listen to window resize for full window changes
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        if (renderer) {
          renderer.dispose();
        }
      };
    } catch {
      setWebglUnavailable(true);
      return undefined;
    }
  }, [canvas3dRef]);

  return (
    <div
      className="bg-[#222222] relative overflow-hidden w-full h-full"
      data-testid="visualization-area"
    >
      <canvas
        ref={canvas3dRef}
        id="canvas3d"
        data-testid="3d-canvas"
        className="w-full h-full block"
      ></canvas>
      {webglUnavailable && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-[#222222] text-gray-400 text-center p-4"
          data-testid="3d-canvas-webgl-unavailable"
        >
          <p>
            3D view is not available in this environment (WebGL not supported).
          </p>
        </div>
      )}

      <PlaybackControls
        isPlaying={isPlaying}
        playbackSpeed={playbackSpeed}
        currentTime={currentTime}
        minTime={minTime}
        maxTime={maxTime}
        disabled={disabled}
        onPlayPause={onPlayPause}
        onPlaybackSpeedChange={onPlaybackSpeedChange}
        onSkipBackward={onSkipBackward}
        onSkipForward={onSkipForward}
        onTimelineChange={onTimelineChange}
      />

      <SelectionsPanel
        selectedPitches={selectedPitches}
        movements={movements}
        colorPalette={colorPalette}
        avatarOffsets={avatarOffsets}
        avatarRotations={avatarRotations}
        onColorChangeAll={selectionHandlers.onColorChangeAll}
        onOffsetChangeAll={selectionHandlers.onOffsetChangeAll}
        onRotationChangeAll={selectionHandlers.onRotationChangeAll}
      />
    </div>
  );
};
